import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { formatDate } from "../../api/dates";
import { percent, useRedListReport, type RedListReport } from "../../api/reports";
import { Card, Stat } from "../../components/ui";
import "../../styles/routing.css";
import "../../styles/visits-dashboard.css";
import "../../styles/redlist-report.css";
import ReportsTabs from "./ReportsTabs";
import { RedListTracker, statusClass, type TrackerFocus } from "./RedListTracker";

/** The Red List progress report: the client's workbook -- its Dashboard, its
 * PRV Tracker and its Notes -- read live off the register. The cards, the
 * breakdown and the contractors' loads are the sheet's; pressing one narrows
 * the tracker underneath. "Download Excel" writes the same report as the
 * workbook the client is used to, formulas and all. */

const DEFAULT_TARGET = 2;
/** The server's bounds for the target; anything else is a 422. */
const MIN_TARGET = 1;
const MAX_TARGET = 50;

/** A whole number within the server's bounds, the default for nonsense. */
export function clampTarget(raw: string | null): number {
  const value = Math.trunc(Number(raw));
  if (!Number.isFinite(value) || value === 0) {
    return DEFAULT_TARGET;
  }
  return Math.min(MAX_TARGET, Math.max(MIN_TARGET, value));
}

export default function RedListReportPage() {
  const [params, setParams] = useSearchParams();
  const target = clampTarget(params.get("target"));
  const report = useRedListReport(target === DEFAULT_TARGET ? null : target);
  const data = report.data;

  function setTarget(next: number) {
    const search = new URLSearchParams(params);
    if (next === DEFAULT_TARGET) {
      search.delete("target");
    } else {
      search.set("target", String(next));
    }
    setParams(search, { replace: true });
  }

  return (
    <div className="page-bleed routing-page">
      <ReportsTabs />
      <div className="routing-content visits-dash">
        <header className="schedule-head">
          <h1>PRV Red List progress report</h1>
          {data ? <span className="report-period">as at {formatDate(data.today)}</span> : null}
          {data ? (
            <div className="schedule-nav">
              <a className="btn btn-primary" href={data.urls.xlsx}>
                Download Excel
              </a>
            </div>
          ) : null}
        </header>
        {report.isPending ? <p className="app-loading">Loading the report…</p> : null}
        {report.isError ? <p className="notice bad">The report could not be loaded: {report.error.message}</p> : null}
        {data ? <Body report={data} target={target} onTarget={setTarget} refreshing={report.isPlaceholderData} /> : null}
      </div>
    </div>
  );
}

function Body({ report, target, onTarget, refreshing }: { report: RedListReport; target: number; onTarget: (next: number) => void; refreshing: boolean }) {
  const [focus, setFocus] = useState<TrackerFocus>(null);
  const cards = report.cards;
  const pick = (next: NonNullable<TrackerFocus>) =>
    setFocus(focus?.kind === next.kind && focus.key === next.key ? null : next);
  const pressed = (kind: "card" | "status", key: string) => focus?.kind === kind && focus.key === key;

  return (
    <div className={refreshing ? "vd-body vd-refreshing" : "vd-body"}>
      <p className="muted rl-source">Source: {report.source}</p>
      <div className="stat-strip" aria-label="Cards">
        <Stat label="Total work orders" value={cards.total} pressed={focus === null} onClick={() => setFocus(null)} />
        <Stat
          label={`Completed · ${percent(cards.completion)}`}
          value={cards.completed}
          good
          tone="var(--ok-ink)"
          pressed={pressed("card", "Completed")}
          onClick={() => pick({ kind: "card", key: "Completed" })}
        />
        <Stat label="Still open" value={cards.still_open} />
        <Stat label="Date set" value={cards.date_set} tone="var(--info)" pressed={pressed("card", "Date Set")} onClick={() => pick({ kind: "card", key: "Date Set" })} />
        <Stat label="Confirmed" value={cards.confirmed} />
        <Stat label="Awaiting confirmation" value={cards.awaiting} tone="var(--warning)" />
        <Stat label="No date yet" value={cards.no_date_yet} tone="var(--warning)" pressed={pressed("card", "No Date Yet")} onClick={() => pick({ kind: "card", key: "No Date Yet" })} />
        <Stat label="Site visited" value={cards.visited} />
      </div>

      <div className="vd-grid">
        <Card title="Work status breakdown" sub="which card each status feeds" className="vd-span-6" flush>
          <div className="table-card">
            <table className="data-table" aria-label="Work status breakdown">
              <thead>
                <tr>
                  <th>Work status</th>
                  <th>Card it feeds</th>
                  <th className="num">Orders</th>
                  <th className="num">% of total</th>
                  <th className="num">Visited</th>
                  <th className="num">% visited</th>
                </tr>
              </thead>
              <tbody>
                {report.breakdown.map((line) => (
                  <tr
                    key={line.work_status}
                    className={pressed("status", line.work_status) ? "clickable selected" : "clickable"}
                    tabIndex={0}
                    onClick={() => pick({ kind: "status", key: line.work_status })}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        pick({ kind: "status", key: line.work_status });
                      }
                    }}
                  >
                    <td>
                      <span className={`rl-chip ${statusClass(line.work_status)}`}>{line.work_status}</span>
                    </td>
                    <td className="muted">{line.card}</td>
                    <td className="num">{line.orders}</td>
                    <td className="num">{percent(line.share)}</td>
                    <td className="num">{line.visited}</td>
                    <td className="num">{percent(line.visited_share)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th>Total</th>
                  <th />
                  <th className="num">{cards.total}</th>
                  <th className="num">100%</th>
                  <th className="num">{cards.visited}</th>
                  <th className="num">{percent(cards.total === 0 ? 0 : cards.visited / cards.total)}</th>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>

        <div className="vd-span-6 rl-side">
          <Card title="Needs attention">
            <dl className="rl-attention">
              <dt>Average days overdue</dt>
              <dd>{report.attention.avg_days_overdue ?? 0}</dd>
              <dt>Longest overdue (days)</dt>
              <dd>{report.attention.longest_overdue ?? 0}</dd>
              <dt>Open 30+ days overdue</dt>
              <dd>{report.attention.open_30_plus ?? 0}</dd>
              <dt>Dates changed after confirmation</dt>
              <dd>{report.attention.dates_changed ?? 0}</dd>
              <dt>Not yet visited</dt>
              <dd>{report.attention.not_yet_visited ?? 0}</dd>
            </dl>
          </Card>
          <Card title="Contractor workload" sub="date set, confirmed, awaiting, visited" flush>
            <div className="table-card">
              <table className="data-table" aria-label="Contractor workload">
                <thead>
                  <tr>
                    <th>Contractor</th>
                    <th className="num">Date set</th>
                    <th className="num">Confirmed</th>
                    <th className="num">Awaiting</th>
                    <th className="num">Visited</th>
                  </tr>
                </thead>
                <tbody>
                  {report.workload.map((line) => (
                    <tr key={line.contractor}>
                      <td>{line.contractor}</td>
                      <td className="num">{line.date_set}</td>
                      <td className="num">{line.confirmed}</td>
                      <td className="num">{line.awaiting}</td>
                      <td className="num">{line.visited}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="rl-total">
                    <th>Total</th>
                    <th className="num">{cards.date_set}</th>
                    <th className="num">{cards.confirmed}</th>
                    <th className="num">{cards.awaiting}</th>
                    <th className="num">{cards.visited}</th>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </div>

        <Card
          title="Daily schedule"
          sub="proposed visits per working day"
          className="vd-span-12"
          actions={
            <label className="rl-target">
              {report.contractors[0] ?? "Lead"} target per day
              <input
                key={target}
                type="number"
                className="input"
                min={MIN_TARGET}
                max={MAX_TARGET}
                defaultValue={target}
                onBlur={(event) => onTarget(clampTarget(event.target.value))}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    onTarget(clampTarget(event.currentTarget.value));
                  }
                }}
              />
            </label>
          }
          flush
        >
          <div className="table-card">
            <table className="data-table" aria-label="Daily schedule">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Day</th>
                  {report.contractors.map((name) => (
                    <th key={name} className="num">
                      {name}
                    </th>
                  ))}
                  <th className="num">Not assigned</th>
                  <th className="num">Total</th>
                  <th className="num">Confirmed</th>
                  <th>{report.contractors[0] ?? "Lead"} vs target</th>
                </tr>
              </thead>
              <tbody>
                {report.days.length === 0 ? (
                  <tr>
                    <td colSpan={6 + report.contractors.length} className="muted">
                      No visits proposed yet.
                    </td>
                  </tr>
                ) : (
                  report.days.map((plan) => (
                    <tr key={plan.day}>
                      <td>{formatDate(plan.day)}</td>
                      <td className="muted">{new Date(`${plan.day}T00:00:00`).toLocaleDateString("en-ZA", { weekday: "short" })}</td>
                      {report.contractors.map((name) => (
                        <td key={name} className="num">
                          {plan.proposed[name] ?? 0}
                        </td>
                      ))}
                      <td className="num">{plan.unassigned}</td>
                      <td className="num">{plan.total}</td>
                      <td className="num">{plan.confirmed}</td>
                      <td className={plan.lead_vs_target === "Below target" ? "rl-below" : undefined}>{plan.lead_vs_target}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <RedListTracker rows={report.rows} focus={focus} onFocus={setFocus} />
    </div>
  );
}
